import { motion, AnimatePresence } from 'framer-motion'
import { Link, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowRight,
  Info,
  Mic,
  FileText,
  CircleAlert,
  Sparkles,
  CalendarClock,
  ShieldAlert,
  UserCheck,
} from 'lucide-react'
import { steps, funnelUrl } from '../content/aiOpsSuite'
import { stepIcons } from '../content/aiOpsIcons'
import UIScreens, { screensFor } from '../components/UIScreens'

// Guided demo v1: an illustrative, scripted tour of the AI Operations Suite.
// The panels are hand-built representations of the real UI, not screenshots
// or live data — every scene says so. Swap in recorded clips once the assets
// in docs/arcs/AI_OPERATIONS_SUITE_ARC.md §4 exist.

const scenario =
  'A small records team has loaded a batch of scanned lease agreements into their Graffiti™ unit. Here is one question, followed from ask to audit.'

const narration = {
  'prompt-shaping':
    'They ask in plain language. Prompt Shaping grounds the request against their own Collections, ranks which Tribe should handle it, and — because two routes are close — asks one clarifying question instead of guessing.',
  docflow:
    'They pick the matching leases and open them in DocFlow. The scanned pages have already been read and indexed on their own unit, so they can ask follow-up questions in plain language and see which passages each answer is drawn from.',
  'conductor-canvas':
    'They want this to happen every time new leases arrive. The same steps go onto the Conductor Canvas — ingest, extract, human review, deliver — and activating it deploys a DAG to Airflow on their unit.',
  'workflow-catalog':
    'The workflow now sits in their Workflow Catalog. The Scheduler runs it nightly and shows each run in the live queue.',
  attribution:
    'Same suite, another space. The leasing office wants to know which outreach actually led to signed leases. Attribution (early access) weights every touchpoint in each prospect\'s journey, and the same journey always gets the same, explainable weights.',
  governed:
    'A request to export tenant personal data to an outside address is stopped by policy and sent to a person for review. Every step, allowed or blocked, lands in the audit trail.',
}

function Panel({ title, children }) {
  return (
    <div className="rounded-2xl border border-void-700/60 bg-void-950 shadow-2xl shadow-void-950/80 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-void-800 bg-void-900/80">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-void-700" />
          <span className="w-2.5 h-2.5 rounded-full bg-void-700" />
          <span className="w-2.5 h-2.5 rounded-full bg-void-700" />
        </div>
        <span className="font-mono text-[11px] text-void-500">{title}</span>
        <span className="text-[10px] uppercase tracking-wider text-ember-400/80">Illustrative</span>
      </div>
      <div className="p-5 font-mono text-[13px] text-void-300">{children}</div>
    </div>
  )
}

function Bar({ value, tone = 'bg-synth-500' }) {
  return (
    <div className="h-1.5 rounded-full bg-void-800 overflow-hidden">
      <div className={`h-full ${tone}`} style={{ width: `${value}%` }} />
    </div>
  )
}

function PromptShapingMock() {
  return (
    <Panel title="prompt-shaping · console">
      <div className="flex items-center gap-2 p-3 rounded-lg bg-void-900 border border-void-800 mb-4">
        <Mic className="w-4 h-4 text-synth-400 shrink-0" />
        <span className="text-void-100">which leases let the tenant sublet without asking?</span>
      </div>
      <p className="text-[11px] uppercase tracking-wider text-void-500 mb-2">Grounding</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {['Collection: Leases 2018–2023', 'Entity: sublet clause', 'Entity: landlord consent'].map((c) => (
          <span key={c} className="px-2 py-1 rounded-md bg-synth-900/40 border border-synth-700/40 text-synth-200 text-xs">{c}</span>
        ))}
      </div>
      <p className="text-[11px] uppercase tracking-wider text-void-500 mb-2">Route candidates</p>
      <div className="space-y-2 mb-4">
        {[['ForensIQ · document review', 62], ['Academic · research assistant', 55]].map(([n, v]) => (
          <div key={n}>
            <div className="flex justify-between text-xs mb-1"><span>{n}</span><span className="text-void-500">example score</span></div>
            <Bar value={v} />
          </div>
        ))}
      </div>
      <div className="p-3 rounded-lg bg-ember-500/10 border border-ember-500/30 text-ember-400 text-xs flex gap-2">
        <CircleAlert className="w-4 h-4 shrink-0" />
        Two routes are close. Do you want a list of matching leases, or an analysis of how the clause is worded?
      </div>
    </Panel>
  )
}

function DocFlowMock() {
  return (
    <Panel title="docflow · Lease_D.pdf">
      <div className="space-y-3 mb-4 font-sans text-sm">
        <p className="ml-auto w-fit max-w-[85%] px-3 py-2 rounded-xl bg-synth-600/30 border border-synth-600/40 text-void-100">
          Can this tenant sublet without asking?
        </p>
        <p className="max-w-[90%] px-3 py-2 rounded-xl bg-void-900 border border-void-800 text-void-200 leading-relaxed">
          Yes, for terms under 12 months. Longer sublets need the landlord&apos;s written consent.
        </p>
        <p className="ml-auto w-fit max-w-[85%] px-3 py-2 rounded-xl bg-synth-600/30 border border-synth-600/40 text-void-100">
          Who has to be notified?
        </p>
        <p className="max-w-[90%] px-3 py-2 rounded-xl bg-void-900 border border-void-800 text-void-200 leading-relaxed">
          The property manager, in writing, 30 days before the sublet starts.
        </p>
      </div>
      <p className="text-[11px] uppercase tracking-wider text-void-500 mb-2">Drawn from</p>
      {['p.6 · §9(b) Subletting', 'p.6 · §9(c) Notice'].map((r) => (
        <div key={r} className="flex items-center gap-2 p-2 mb-2 rounded-md bg-void-900 border border-void-800 text-xs">
          <FileText className="w-3.5 h-3.5 text-signal-400" /> Lease_D.pdf · {r}
        </div>
      ))}
    </Panel>
  )
}

function CanvasMock() {
  const nodes = [
    { label: 'Ingest', tag: 'SOURCE', color: 'border-t-signal-400' },
    { label: 'Extract clauses', tag: 'ENRICH', color: 'border-t-synth-400' },
    { label: 'Human review', tag: 'REVIEW', color: 'border-t-ember-400' },
    { label: 'Deliver report', tag: 'SERVE', color: 'border-t-emerald-400' },
  ]
  return (
    <Panel title="conductor · canvas">
      <div className="relative grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 rounded-lg bg-[radial-gradient(circle,_rgba(148,163,184,0.12)_1px,_transparent_1px)] [background-size:16px_16px]">
        {nodes.map((n, i) => (
          <div key={n.label} className="relative">
            <div className={`p-3 rounded-lg bg-void-900 border border-void-700 border-t-2 ${n.color}`}>
              <p className="text-[10px] text-void-500">{n.tag}</p>
              <p className="text-void-100 text-xs font-sans">{n.label}</p>
            </div>
            {i < nodes.length - 1 && (
              <ArrowRight className="hidden sm:block absolute top-1/2 -right-3 -translate-y-1/2 w-3 h-3 text-void-500" />
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-4 text-xs">
        <span className="text-void-500">lease_intake · saved</span>
        <span className="px-3 py-1 rounded-md bg-synth-600 text-white font-sans">Activate → deploy DAG</span>
      </div>
    </Panel>
  )
}

function SchedulerMock() {
  const runs = [
    ['lease_intake', 'running', 'text-signal-300'],
    ['lease_intake', 'success', 'text-emerald-300'],
    ['monthly_rollup', 'queued', 'text-void-400'],
  ]
  return (
    <Panel title="scheduler · run queue">
      <div className="flex items-center gap-2 mb-3 text-xs text-void-500">
        <CalendarClock className="w-4 h-4" /> lease_intake · <span className="text-void-200">0 2 * * *</span> · nightly at 02:00
      </div>
      <div className="rounded-lg border border-void-800 overflow-hidden">
        {runs.map(([w, st, tone], i) => (
          <div key={i} className={`flex justify-between px-3 py-2 text-xs ${i ? 'border-t border-void-800' : ''}`}>
            <span>{w}</span>
            <span className={tone}>{st}</span>
          </div>
        ))}
      </div>
    </Panel>
  )
}

function AttributionMock() {
  const touches = [
    ['Listing site visit', 18],
    ['Email reply', 30],
    ['Guided tour', 52],
    ['Application started', 84],
  ]
  return (
    <Panel title="attribution · journey">
      <p className="text-xs text-void-500 mb-3">Prospect journey → <span className="text-emerald-300">lease signed</span></p>
      <div className="space-y-3 mb-4">
        {touches.map(([t, v]) => (
          <div key={t}>
            <div className="flex justify-between text-xs mb-1"><span>{t}</span><span className="text-void-500">example weight</span></div>
            <Bar value={v} tone="bg-signal-500" />
          </div>
        ))}
      </div>
      <p className="text-xs text-void-400">Fibonacci Decay · sized to this journey&apos;s 4 touchpoints · deterministic</p>
    </Panel>
  )
}

function GovernedMock() {
  return (
    <Panel title="23controls · policy gate">
      <div className="p-3 rounded-lg bg-red-900/20 border border-red-700/30 text-red-200 text-xs flex gap-2 mb-3">
        <ShieldAlert className="w-4 h-4 shrink-0" />
        Blocked: export of tenant personal data to an external address. Escalated for review.
      </div>
      <div className="p-3 rounded-lg bg-void-900 border border-void-800 text-xs flex gap-2 mb-3">
        <UserCheck className="w-4 h-4 text-synth-400 shrink-0" /> Review queue: 1 item awaiting a person
      </div>
      <p className="text-[11px] uppercase tracking-wider text-void-500 mb-2">Audit trail</p>
      {['ask · routed · anchored identity', 'answer · 3 citations verified', 'workflow · activated', 'export · blocked by policy'].map((a) => (
        <p key={a} className="text-xs text-void-400 py-0.5">› {a}</p>
      ))}
    </Panel>
  )
}

const mocks = {
  'prompt-shaping': PromptShapingMock,
  docflow: DocFlowMock,
  'conductor-canvas': CanvasMock,
  'workflow-catalog': SchedulerMock,
  attribution: AttributionMock,
  governed: GovernedMock,
}

export default function DemoPage() {
  const [params, setParams] = useSearchParams()
  const requested = steps.findIndex((s) => s.id === params.get('scene'))
  const idx = requested === -1 ? 0 : requested
  const scene = steps[idx]
  const Mock = mocks[scene.id]
  const screens = screensFor(scene.id)
  const anyActual = steps.some((s) => screensFor(s.id).length > 0)
  const go = (i) => setParams({ scene: steps[i].id }, { replace: true })

  return (
    <div className="pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <p className="text-xs font-semibold text-synth-400 uppercase tracking-wider mb-3">Guided demo · AI Operations Suite</p>
          <h1 className="text-4xl md:text-5xl font-display font-bold text-void-50 mb-4">
            From one question to a <span className="text-gradient">governed workflow</span>
          </h1>
          <p className="text-void-400 max-w-2xl mx-auto">{scenario}</p>
        </div>

        <div className="flex items-start gap-2 p-3 rounded-xl bg-void-900/40 border border-void-800/60 text-void-400 text-xs max-w-3xl mx-auto mb-10">
          <Info className="w-4 h-4 shrink-0 mt-0.5 text-signal-400" />
          <p>
            {anyActual && 'Panels marked "Actual interface" are captures of 23controls.com taken with demo data. '}
            {anyActual ? 'The rest are illustrative panels' : 'This tour uses illustrative panels'} that represent the product's interface. Documents, names, and
            scores are examples, not live data or performance results. For the real thing, request a live
            walkthrough below.
          </p>
        </div>

        <ol className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-10">
          {steps.map((s, i) => {
            const Icon = stepIcons[s.id]
            const active = i === idx
            return (
              <li key={s.id}>
                <button
                  onClick={() => go(i)}
                  className={`w-full flex flex-col items-center gap-2 p-3 rounded-xl border transition-colors ${
                    active ? 'bg-synth-900/40 border-synth-600/60 text-synth-200' : 'bg-void-900/30 border-void-800/60 text-void-500 hover:text-void-300'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-xs font-medium">{i + 1}. {s.verb}</span>
                </button>
              </li>
            )
          })}
        </ol>

        <AnimatePresence mode="wait">
          <motion.div
            key={scene.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="grid lg:grid-cols-5 gap-8 items-start mb-10"
          >
            <div className="lg:col-span-2">
              <p className="font-mono text-xs text-void-500 mb-2">Scene {idx + 1} of {steps.length}</p>
              <h2 className="text-2xl font-display font-bold text-void-50 mb-3">{scene.verb}: {scene.name}</h2>
              <p className="text-void-300 leading-relaxed mb-5">{narration[scene.id]}</p>
              <Link to={`/ai-operations/${scene.id}`} className="inline-flex items-center gap-2 text-synth-400 hover:text-synth-300 text-sm font-medium">
                How {scene.name} works <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="lg:col-span-3">
              {screens.length > 0 ? <UIScreens key={scene.id} screens={screens} /> : <Mock />}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between gap-4 pt-6 border-t border-void-800/50 mb-16">
          <button
            onClick={() => go(idx - 1)}
            disabled={idx === 0}
            className="inline-flex items-center gap-2 text-void-400 hover:text-void-200 disabled:opacity-30 disabled:pointer-events-none"
          >
            <ArrowLeft className="w-4 h-4" /> Previous
          </button>
          {idx < steps.length - 1 ? (
            <button onClick={() => go(idx + 1)} className="btn-primary inline-flex items-center gap-2">
              Next scene <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <a href={funnelUrl('/signup', 'demo-end')} className="btn-primary inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Start your subscription
            </a>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-6 rounded-2xl bg-void-900/30 border border-void-800/50">
            <h3 className="font-display font-semibold text-void-100 mb-2">See it live, with your own documents</h3>
            <p className="text-void-400 text-sm mb-4">
              A person from our team will walk you through the suite running on a real unit. No self-serve
              sandbox yet — this goes to a human.
            </p>
            <Link to="/support/contact/sales" className="btn-secondary inline-flex items-center gap-2 text-sm">
              Request a live walkthrough <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="p-6 rounded-2xl bg-gradient-to-br from-synth-900/50 to-void-900 border border-synth-800/30">
            <h3 className="font-display font-semibold text-void-100 mb-2">Ready to start?</h3>
            <p className="text-void-400 text-sm mb-4">
              The AI Operations Suite comes with your 23Controls™ subscription. Create your account and
              Identity Anchor to begin.
            </p>
            <a href={funnelUrl('/signup', 'demo-card')} className="btn-primary inline-flex items-center gap-2 text-sm">
              <Sparkles className="w-4 h-4" /> Start your subscription
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
